import { Component } from "react";

import DataTable from "components/DigiHapi/DataTable";
import {
	DragTrComponent,
	DropTbodyComponent
} from "components/DigiHapi/DraggableTable";
import { DragDropContext } from "react-beautiful-dnd";

// @mui material components
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";

// DigiHapi components
import FormModal from "components/DigiHapi/FormModal";
import FormSelect from "components/DigiHapi/FormSelect";
import Notification from "components/DigiHapi/Notification";

import { connect } from "react-redux";
import { bindActionCreators } from "redux";

import { fetchGetGoodDealsCategories } from "actions/gooddeal";
import {
	fetchAddGoodDealCategory,
	fetchUpdateGoodDealCategory,
	fetchReorderGoodDealsCategories,
	fetchGetGoodDealsCategoriesPictos
} from "actions/gooddealCategory";

const COLOR_REGEX = /^#[0-9a-f]{6}$/i;

const EMPTY_FORM = { name: "", nameEn: "", picto: "", bgColor: "" };

// Pictogram on its category background color
function CategoryPicto({ uri, bgColor, size = "2.25rem" }) {
	return (
		<MDBox
			display="flex"
			alignItems="center"
			justifyContent="center"
			width={size}
			height={size}
			borderRadius="50%"
			sx={{
				backgroundColor: bgColor || "transparent",
				border: bgColor ? "none" : "1px dashed #d2d6da"
			}}
		>
			{uri && (
				<MDBox
					component="img"
					src={uri}
					alt=""
					width="70%"
					height="70%"
					sx={{ objectFit: "contain" }}
				/>
			)}
		</MDBox>
	);
}

/**
 * Good deals categories (webmaster): add, edit and reorder by drag and drop.
 * The order is the one of the categories in the apps.
 */
class GoodDealsCategoriesPage extends Component {
	state = {
		// Categories being reordered (null: server order)
		reordered: null,
		// Category being added / edited (null: no form)
		form: null,
		formErrors: {},
		br: false,
		color: "info",
		message: null
	};

	componentDidMount() {
		this.props.fetchGetGoodDealsCategories();
		this.props.fetchGetGoodDealsCategoriesPictos();
	}

	componentDidUpdate(prevProps) {
		const { snackErrorMsg, snackInfoMsg } = this.props;

		if (snackInfoMsg && prevProps.snackInfoMsg !== snackInfoMsg) {
			this.setState({ form: null, reordered: null });
			this.showMessage(snackInfoMsg, "info");
		}
		if (snackErrorMsg && prevProps.snackErrorMsg !== snackErrorMsg) {
			this.showMessage(snackErrorMsg, "danger");
		}
	}

	showMessage = (message, color) => {
		this.setState({ br: true, message, color });
		if (color === "info") {
			setTimeout(this.hideAlert, 5000);
		}
	};

	hideAlert = () => {
		this.setState({ br: false });
	};

	getCategories = () => this.state.reordered || this.props.categories;

	handleDragEnd = ({ source, destination }) => {
		// Dropped outside the list
		if (!destination || destination.index === source.index) {
			return;
		}
		const categories = [...this.getCategories()];
		const [moved] = categories.splice(source.index, 1);
		categories.splice(destination.index, 0, moved);
		this.setState({ reordered: categories });
	};

	saveOrder = () => {
		this.props.fetchReorderGoodDealsCategories(
			this.state.reordered.map(({ _id }) => _id)
		);
	};

	openForm = (category = null) => {
		this.setState({
			form: category
				? {
						_id: category._id,
						name: category.name || "",
						nameEn: category.lang?.en?.name || "",
						picto: category.picto || "",
						bgColor: category.bgColor || ""
				  }
				: { ...EMPTY_FORM },
			formErrors: {}
		});
	};

	closeForm = () => {
		this.setState({ form: null, formErrors: {} });
	};

	setFormValue = (key, value) => {
		this.setState(({ form, formErrors }) => ({
			form: { ...form, [key]: value },
			formErrors: { ...formErrors, [key]: null }
		}));
	};

	submitForm = () => {
		const { form } = this.state;
		const name = form.name.trim();
		const bgColor = form.bgColor.trim();

		const formErrors = {};
		if (!name) {
			formErrors.name = "Le nom est obligatoire";
		}
		if (bgColor && !COLOR_REGEX.test(bgColor)) {
			formErrors.bgColor = "Couleur au format #RRGGBB";
		}
		if (Object.keys(formErrors).length) {
			this.setState({ formErrors });
			return;
		}

		const category = {
			name,
			lang: { en: { name: form.nameEn.trim() } },
			picto: form.picto || undefined,
			bgColor: bgColor || undefined
		};
		if (form._id) {
			this.props.fetchUpdateGoodDealCategory(form._id, category);
		} else {
			this.props.fetchAddGoodDealCategory(category);
		}
	};

	renderForm = () => {
		const { form, formErrors } = this.state;
		const { pictos, isFetching } = this.props;
		const colorValue = COLOR_REGEX.test(form.bgColor)
			? form.bgColor
			: "#ffffff";

		return (
			<FormModal maxWidth="sm">
				<Card>
					<MDBox
						mx={2}
						mt={-3}
						py={2}
						px={2}
						variant="gradient"
						bgColor="primary"
						borderRadius="lg"
						coloredShadow="primary"
						color="white"
					>
						<MDTypography variant="h6" color="white">
							{form._id
								? "Modification d'une catégorie"
								: "Ajout d'une catégorie"}
						</MDTypography>
					</MDBox>
					<MDBox p={3}>
						<Grid container spacing={3}>
							<Grid item xs={12} sm={6}>
								<MDInput
									label="Nom *"
									fullWidth
									value={form.name}
									error={!!formErrors.name}
									helperText={formErrors.name}
									onChange={e => this.setFormValue("name", e.target.value)}
									disabled={isFetching}
								/>
							</Grid>
							<Grid item xs={12} sm={6}>
								<MDInput
									label="Nom en anglais"
									fullWidth
									value={form.nameEn}
									onChange={e => this.setFormValue("nameEn", e.target.value)}
									disabled={isFetching}
								/>
							</Grid>
							<Grid item xs={12} sm={6}>
								<FormSelect
									label="Pictogramme"
									value={form.picto}
									placeholder="Aucun"
									placeholderSelectable
									onChange={e => this.setFormValue("picto", e.target.value)}
									disabled={isFetching}
								>
									<MenuItem value="">Aucun</MenuItem>
									{Object.entries(pictos).map(([name, uri]) => (
										<MenuItem key={name} value={name}>
											<MDBox
												component="img"
												src={uri}
												alt=""
												width="1.5rem"
												height="1.5rem"
												mr={1}
												sx={{ objectFit: "contain" }}
											/>
											{name}
										</MenuItem>
									))}
								</FormSelect>
							</Grid>
							<Grid item xs={12} sm={6}>
								<MDBox display="flex" alignItems="flex-start" gap={1}>
									<MDInput
										label="Couleur de fond"
										placeholder="#49accd"
										fullWidth
										value={form.bgColor}
										error={!!formErrors.bgColor}
										helperText={formErrors.bgColor}
										onChange={e => this.setFormValue("bgColor", e.target.value)}
										disabled={isFetching}
									/>
									<MDBox
										component="input"
										type="color"
										aria-label="Choisir la couleur de fond"
										value={colorValue}
										onChange={e => this.setFormValue("bgColor", e.target.value)}
										disabled={isFetching}
										sx={{
											width: "2.75rem",
											height: "2.75rem",
											p: 0,
											border: "none",
											background: "none",
											cursor: "pointer",
											flexShrink: 0
										}}
									/>
								</MDBox>
							</Grid>
							<Grid item xs={12}>
								<MDBox display="flex" alignItems="center" gap={1.5}>
									<MDTypography variant="button" color="text">
										Aperçu :
									</MDTypography>
									<CategoryPicto
										uri={pictos[form.picto]}
										bgColor={COLOR_REGEX.test(form.bgColor) ? form.bgColor : ""}
										size="3rem"
									/>
								</MDBox>
							</Grid>
						</Grid>
					</MDBox>
					<MDBox p={3} pt={0} display="flex" justifyContent="flex-end" gap={1}>
						<MDButton
							variant="outlined"
							color="secondary"
							onClick={this.closeForm}
							disabled={isFetching}
						>
							Annuler
						</MDButton>
						<MDButton
							variant="gradient"
							color="primary"
							onClick={this.submitForm}
							disabled={isFetching}
						>
							{isFetching ? (
								<CircularProgress size={16} color="inherit" />
							) : form._id ? (
								"Modifier"
							) : (
								"Ajouter"
							)}
						</MDButton>
					</MDBox>
				</Card>
			</FormModal>
		);
	};

	getTableData = () => {
		const { pictos } = this.props;
		return this.getCategories().map(category => ({
			_id: category._id,
			picto: (
				<CategoryPicto
					uri={pictos[category.picto]}
					bgColor={category.bgColor}
				/>
			),
			name: category.name,
			nameEn: category.lang?.en?.name || "",
			actions: (
				<MDBox display="flex" justifyContent="flex-end">
					<Tooltip title="Modifier" placement="bottom">
						<span>
							<MDButton
								variant="gradient"
								color="info"
								size="small"
								iconOnly
								circular
								disabled={!!this.state.reordered}
								onClick={() => this.openForm(category)}
							>
								<Icon>edit</Icon>
							</MDButton>
						</span>
					</Tooltip>
				</MDBox>
			)
		}));
	};

	render() {
		const { reordered, form, color, message, br } = this.state;
		const { isFetching } = this.props;

		return (
			<Grid container spacing={3} justifyContent="center">
				<Grid item xs={12}>
					<Card>
						<MDBox
							mx={2}
							mt={-3}
							py={2}
							px={2}
							variant="gradient"
							bgColor="primary"
							borderRadius="lg"
							coloredShadow="primary"
							display="flex"
							alignItems="center"
							color="white"
						>
							<Icon fontSize="medium">category</Icon>
							<MDTypography variant="h6" color="white" ml={1.5}>
								Catégories de bons plans
							</MDTypography>
						</MDBox>
						<MDBox px={3} pt={2}>
							<MDTypography variant="button" color="text">
								Faites glisser les lignes pour changer l'ordre d'affichage des
								catégories dans les applications.
							</MDTypography>
						</MDBox>
						<MDBox p={2}>
							<DragDropContext onDragEnd={this.handleDragEnd}>
								<DataTable
									TrComponent={DragTrComponent}
									TbodyComponent={DropTbodyComponent}
									data={this.getTableData()}
									columns={[
										{
											Header: "",
											accessor: "picto",
											sortable: false,
											width: "4rem"
										},
										{ Header: "Nom", accessor: "name", sortable: false },
										{
											Header: "Nom en anglais",
											accessor: "nameEn",
											sortable: false
										},
										{ Header: "Actions", accessor: "actions", sortable: false }
									]}
									// No pagination: every category must be reorderable
									defaultPageSize={1000}
									showPaginationTop={false}
									showPaginationBottom={false}
									noDataText="Aucune catégorie"
								/>
							</DragDropContext>
						</MDBox>
						<MDBox
							p={2}
							display="flex"
							flexWrap="wrap"
							justifyContent="flex-end"
							gap={1}
						>
							{reordered ? (
								<>
									<MDButton
										variant="outlined"
										color="secondary"
										onClick={() => this.setState({ reordered: null })}
										disabled={isFetching}
									>
										Restaurer
									</MDButton>
									<MDButton
										variant="gradient"
										color="primary"
										onClick={this.saveOrder}
										disabled={isFetching}
									>
										{isFetching ? (
											<CircularProgress size={16} color="inherit" />
										) : (
											"Enregistrer l'ordre"
										)}
									</MDButton>
								</>
							) : (
								<MDButton
									variant="gradient"
									color="primary"
									onClick={() => this.openForm()}
								>
									<Icon>add</Icon>
									&nbsp;Ajout d'une catégorie
								</MDButton>
							)}
						</MDBox>
					</Card>
				</Grid>
				{form && this.renderForm()}
				<Notification
					place="br"
					color={color}
					message={message}
					open={br}
					onClose={this.hideAlert}
				/>
			</Grid>
		);
	}
}

const mapStateToProps = state => {
	const { isFetching, snackInfoMsg, snackErrorMsg, pictos } =
		state.gooddealCategoryReducer;
	const { goodDealsCategories } = state.entitiesReducer;

	return {
		isFetching,
		snackInfoMsg,
		snackErrorMsg,
		pictos,
		categories: Object.values(goodDealsCategories).sort(
			(a, b) => (a.order || 0) - (b.order || 0)
		)
	};
};

const mapDispatchToProps = dispatch =>
	bindActionCreators(
		{
			fetchGetGoodDealsCategories,
			fetchAddGoodDealCategory,
			fetchUpdateGoodDealCategory,
			fetchReorderGoodDealsCategories,
			fetchGetGoodDealsCategoriesPictos
		},
		dispatch
	);

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(GoodDealsCategoriesPage);
